import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Truck,
  HardHat,
  ShieldCheck,
  Phone,
} from "lucide-react";
import { COMPANY } from "../lib/company";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_haul-connect-8/artifacts/4ef3b9sx_Lastbil%20h%C3%A4mtar%20singel.jpg";
const AKERI_IMG =
  "https://customer-assets.emergentagent.com/job_haul-connect-8/artifacts/pzr2q5vu_MAN%20vs%20Merca%20lastbil.jpg";
const ENTREPRENAD_IMG =
  "https://customer-assets.emergentagent.com/job_haul-connect-8/artifacts/3acpqsym_Screenshot_20260604_175246_Instagram.jpg";

const MARQUEE_ITEMS = [
  "ADR Certifierade",
  "YKB Utbildade Förare",
  "ISO-styrt Arbetssätt",
  "Egna Fordon & Maskiner",
  "Schakt & Transport",
  "Lokalt Förankrade i Haninge",
  "Säkerhet Först",
];

export default function Hem() {
  return (
    <div data-testid="page-hem">
      {/* HERO */}
      <section
        className="relative overflow-hidden bg-black"
        data-testid="hero-section"
      >
        <img
          src={HERO_IMG}
          alt="Humle Åkeri lastbil på väg"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="hero-grain" />

        <div className="relative mx-auto max-w-[1400px] px-6 lg:px-12 py-24 lg:py-40">
          <div className="max-w-4xl">
            <p
              className="overline text-[#FFD700] mb-6"
              data-testid="hero-overline"
            >
              Humlegruppen · Sedan 2020
            </p>
            <h1
              className="font-display font-black text-white text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight"
              data-testid="hero-title"
            >
              Kraft.
              <br />
              Precision.
              <br />
              <span className="text-[#FFD700]">Tillförlitlighet.</span>
            </h1>
            <p
              className="mt-8 text-lg lg:text-xl text-gray-200 max-w-2xl leading-relaxed"
              data-testid="hero-subtitle"
            >
              Två starka verksamheter — ett gemensamt löfte. Humle Åkeri
              transporterar gods över hela Sverige. Humle Entreprenad utför
              schakt, mark- och anläggningsarbeten med tunga maskiner och
              erfarna operatörer.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/tjanster"
                className="inline-flex items-center gap-3 bg-[#FFD700] text-black font-bold px-7 py-4 border-2 border-[#FFD700] hover:bg-yellow-400 transition-colors group"
                data-testid="hero-cta-services"
              >
                <span className="font-condensed uppercase tracking-wider text-base">
                  Våra tjänster
                </span>
                <ArrowUpRight
                  size={20}
                  className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                />
              </Link>
              <a
                href={COMPANY.phoneHref}
                className="inline-flex items-center gap-3 bg-transparent text-white font-bold px-7 py-4 border-2 border-white hover:bg-white hover:text-black transition-colors"
                data-testid="hero-cta-phone"
              >
                <Phone size={18} strokeWidth={2.5} />
                <span className="font-condensed uppercase tracking-wider text-base">
                  {COMPANY.phone}
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom stat bar */}
        <div className="relative border-t-2 border-[#FFD700] bg-black/80 backdrop-blur-sm">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-12 grid grid-cols-2 lg:grid-cols-4 divide-x divide-gray-800">
            {[
              { k: "100+", v: "Samlad erfarenhet i åratal" },
              { k: "2", v: "Verksamhetsgrenar" },
              { k: "100%", v: "Egna fordon" },
              { k: "24/7", v: "Beredskap" },
            ].map((s, i) => (
              <div
                key={i}
                className="py-6 px-4 lg:px-8"
                data-testid={`hero-stat-${i}`}
              >
                <p className="font-display font-black text-3xl lg:text-5xl text-[#FFD700]">
                  {s.k}
                </p>
                <p className="overline text-gray-300 mt-1">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DUAL DIVISIONS */}
      <section
        className="bg-white py-20 lg:py-32"
        data-testid="divisions-section"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
            <div>
              <p className="overline text-gray-500 mb-4">Två verksamheter</p>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-black leading-[1] tracking-tight max-w-3xl">
                En grupp. Två starka verksamheter.
              </h2>
            </div>
            <p className="text-gray-600 text-base lg:text-lg max-w-md leading-relaxed">
              Tillsammans täcker vi hela kedjan från grundläggande markarbete
              till transport av material och färdigt gods.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {/* Åkeri */}
            <div
              className="bg-white border-2 border-black brut-shadow"
              data-testid="division-akeri"
            >
              <div className="aspect-[4/3] overflow-hidden border-b-2 border-black">
                <img
                  src={AKERI_IMG}
                  alt="Lastbil från Humle Åkeri"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#FFD700] flex items-center justify-center border-2 border-black">
                    <Truck size={24} strokeWidth={2.5} />
                  </div>
                  <p className="overline text-gray-500">Verksamhet 01</p>
                </div>
                <h3 className="font-display font-black text-3xl lg:text-4xl mb-3">
                  Humle Åkeri AB
                </h3>
                <p className="text-gray-700 text-base leading-relaxed mb-6">
                  Transporter av bulkmaterial, schaktmassor, grus och anläggn­
                  ingsmaterial. Egen fordonsflotta med tippbilar, kranbilar och
                  trailerkombinationer.
                </p>
                <ul className="space-y-2 mb-8">
                  {[
                    "Lastväxlare",
                    "Kranbilar",
                    "Byggavfallshantering",
                    "Jord & Krossprodukter",
                  ].map((t, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2 text-gray-800"
                    >
                      <span className="w-2 h-2 bg-[#FFD700] border border-black" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/tjanster"
                  className="inline-flex items-center gap-2 font-condensed uppercase tracking-wider font-bold text-black border-b-2 border-black hover:text-[#166534] hover:border-[#166534] transition-colors"
                  data-testid="division-akeri-link"
                >
                  Se transporttjänster <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>

            {/* Entreprenad */}
            <div
              className="bg-black text-white border-2 border-black brut-shadow-yellow"
              data-testid="division-entreprenad"
            >
              <div className="aspect-[4/3] overflow-hidden border-b-2 border-black">
                <img
                  src={ENTREPRENAD_IMG}
                  alt="Grävmaskin Humle Entreprenad"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 lg:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-[#FFD700] flex items-center justify-center border-2 border-[#FFD700]">
                    <HardHat size={24} strokeWidth={2.5} color="#000" />
                  </div>
                  <p className="overline text-[#FFD700]">Verksamhet 02</p>
                </div>
                <h3 className="font-display font-black text-3xl lg:text-4xl mb-3">
                  Humle Entreprenad AB
                </h3>
                <p className="text-gray-300 text-base leading-relaxed mb-6">
                  Schakt, mark- och anläggningsarbeten med tunga maskiner.
                  Vi utför totalentreprenader, rivning och grundläggning åt
                  byggbolag, kommun och privatkunder.
                </p>
                <ul className="space-y-2 mb-8">
                  {[
                    "Schakt & markarbeten",
                    "Rivning & sanering",
                    "Grundläggning",
                    "VA & ledningsarbeten",
                  ].map((t, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-2 text-gray-200"
                    >
                      <span className="w-2 h-2 bg-[#FFD700]" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/tjanster"
                  className="inline-flex items-center gap-2 font-condensed uppercase tracking-wider font-bold text-[#FFD700] border-b-2 border-[#FFD700] hover:text-white hover:border-white transition-colors"
                  data-testid="division-entreprenad-link"
                >
                  Se entreprenadtjänster <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST MARQUEE */}
      <section
        className="bg-[#FFD700] border-y-2 border-black overflow-hidden"
        data-testid="trust-marquee"
      >
        <div className="marquee py-6">
          <div className="marquee-track">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-6 shrink-0"
              >
                <ShieldCheck size={28} strokeWidth={2.5} />
                <span className="font-display font-black text-2xl lg:text-3xl uppercase tracking-tight whitespace-nowrap">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section
        className="bg-[#F3F4F6] py-20 lg:py-32"
        data-testid="why-us-section"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="overline text-gray-500 mb-4">
                Varför Humlegruppen
              </p>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-black leading-[1] tracking-tight">
                Tunga maskiner.
                <br />
                <span className="text-[#166534]">Lättsam dialog.</span>
              </h2>
              <p className="mt-6 text-gray-700 text-lg leading-relaxed">
                Humle Åkeri AB och Humle Entreprenad AB startades 2020 — av
                människor med decenniers samlad erfarenhet i transport- och
                anläggningsbranschen. Det märks i hur vi planerar uppdragen,
                hörs i våra kundrelationer och när du ringer oss — du får
                alltid en person som vet vad du pratar om.
              </p>
              <Link
                to="/om-oss"
                className="inline-flex items-center gap-2 mt-8 font-condensed uppercase tracking-wider font-bold text-black border-b-2 border-black pb-1 hover:text-[#166534] hover:border-[#166534] transition-colors"
                data-testid="why-us-link"
              >
                Läs mer om oss <ArrowUpRight size={18} />
              </Link>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                {
                  k: "01",
                  t: "Egna fordon & maskiner",
                  d: "Hela flottan är vår egen. Det ger flexibilitet och kvalitetskontroll.",
                },
                {
                  k: "02",
                  t: "Erfarna förare & operatörer",
                  d: "Våra medarbetare har YKB, ADR och bred yrkeserfarenhet.",
                },
                {
                  k: "03",
                  t: "Lokalt förankrade",
                  d: "Baserade i Tungelsta, verksamma i hela Stor­stockholm.",
                },
                {
                  k: "04",
                  t: "Helhetsleverantör",
                  d: "Vi tar projekt från första schakt till sista transport.",
                },
              ].map((f, i) => (
                <div
                  key={i}
                  className="bg-white border-2 border-black p-7"
                  data-testid={`why-card-${i}`}
                >
                  <p className="font-display font-black text-5xl text-[#FFD700] mb-3">
                    {f.k}
                  </p>
                  <h3 className="font-display font-bold text-xl mb-2 text-black">
                    {f.t}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section
        className="bg-black text-white py-16 lg:py-24"
        data-testid="cta-strip"
      >
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl">
            Har du ett projekt att flytta framåt?{" "}
            <span className="text-[#FFD700]">Ring oss.</span>
          </h2>
          <a
            href={COMPANY.phoneHref}
            className="inline-flex items-center gap-3 bg-[#FFD700] text-black font-bold px-8 py-5 border-2 border-[#FFD700] hover:bg-yellow-400 transition-colors"
            data-testid="cta-strip-phone"
          >
            <Phone size={22} strokeWidth={2.5} />
            <span className="font-display font-black text-xl lg:text-2xl">
              {COMPANY.phone}
            </span>
          </a>
        </div>
      </section>
    </div>
  );
}
